import { QueryClient, QueryCache, MutationCache, QueryFunction } from "@tanstack/react-query";
import { clearAccessError, reportAccessError } from "./accessErrorStore";
import { handleBilling402 } from "./billingGate";
import { COMPANY_ME_QUERY_KEY, isModuleDisabledError } from "./edition";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let errorData: any = {};
    const text = await res.text();
    
    try {
      // Try to parse as JSON for structured error information
      errorData = JSON.parse(text);
    } catch {
      // Fallback to plain text
      errorData = { error: text || res.statusText };
    }
    
    // Create a structured error object for access control
    const error = new Error(errorData.error || `${res.status}: ${res.statusText}`);
    (error as any).status = res.status;
    (error as any).data = errorData;
    
    throw error;
  }
}

// `method` is a verb UNION on purpose: with both params typed `string`, a swapped
// (url, method) call compiles clean and only fails at runtime — the fetch literally
// requests the URL "POST" (this exact bug shipped in ContentLibrary's approve button).
export async function apiRequest(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  url: string,
  data?: unknown | undefined,
): Promise<any> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return await res.json();
}

export async function apiUploadFiles(
  url: string,
  formData: FormData,
): Promise<any> {
  const res = await fetch(url, {
    method: "POST",
    body: formData,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return await res.json();
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Global access-gate error handler (Phase 5 item 4): surface any 402/403 from any query/mutation via the
// AccessDeniedModal instead of leaving a dead shell. On a billing 402 (e.g. a trial that lapsed
// mid-session) also re-evaluate the access gate by invalidating /api/auth/me so the App routes the user to
// the payment step. /api/auth itself never 402s, but if /api/auth/me still reports access while product APIs
// 402, every re-check refetched the page and 402'd again (request loop until 429, Tag-8 check 2026-10-04).
// So each 402 code re-checks /api/auth/me once; a repeat routes to the PaymentStep (lib/billingGate.ts).
function handleGlobalError(error: unknown) {
  // Hotel-Edition: a module switched off server-side is not an access problem. Refetch the capability so
  // the menu and the route lock drop the module (PLAN.md §14 A-1); no AccessDeniedModal.
  if (isModuleDisabledError(error)) {
    queryClient.invalidateQueries({ queryKey: COMPANY_ME_QUERY_KEY });
    return;
  }
  const billing = handleBilling402(error);
  if (billing === "payment") {
    // The PaymentStep replaces the page; no AccessDeniedModal on top of it.
    clearAccessError();
    return;
  }
  const handled = reportAccessError(error);
  if (handled && billing === "recheck") {
    queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleGlobalError }),
  mutationCache: new MutationCache({ onError: handleGlobalError }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
